import React, { useMemo, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Baby, TrendingUp, ChevronRight, CheckCircle2, Circle, ClipboardEdit, Sparkles, AlertTriangle } from 'lucide-react';
import { draftsAPI } from '../lib/api';
import {
    MODULOS_PARVULARIA,
    INDICADORES_PARVULARIA,
    RUBRICAS_PARVULARIA,
    NIVEL_RIESGO_STYLE,
} from '../lib/parvularia-data';

/**
 * Estado por rúbrica basado en borradores existentes del usuario:
 *   - no_iniciada: no hay borrador
 *   - en_desarrollo: hay borrador sin análisis IA
 *   - analizada: hay borrador con feedback IA
 *   - lista: hay borrador con nivel_estimado Competente o Destacado
 */
function computeEstadoRubrica(drafts, indicadorId) {
    const ds = drafts.filter((d) => d.indicador_id === indicadorId);
    if (ds.length === 0) return { estado: 'no_iniciada', count: 0 };
    const withFeedback = ds.filter((d) => d.feedback);
    if (withFeedback.length === 0) return { estado: 'en_desarrollo', count: ds.length };
    const best = withFeedback.find((d) => ['Competente', 'Destacado'].includes(d.nivel_estimado));
    if (best) return { estado: 'lista', count: ds.length, nivel: best.nivel_estimado };
    return { estado: 'analizada', count: ds.length };
}

const ESTADO_STYLE = {
    no_iniciada: { color: 'slate', label: 'No iniciada', icon: Circle },
    en_desarrollo: { color: 'amber', label: 'En desarrollo', icon: ClipboardEdit },
    analizada: { color: 'indigo', label: 'Analizada', icon: Sparkles },
    lista: { color: 'emerald', label: 'Lista', icon: CheckCircle2 },
};

const RIESGO_BADGE = {
    emerald: 'bg-emerald-100 text-emerald-700 border-emerald-200',
    amber: 'bg-amber-100 text-amber-700 border-amber-200',
    orange: 'bg-orange-100 text-orange-700 border-orange-200',
    rose: 'bg-rose-100 text-rose-700 border-rose-200',
};

export default function ProgresoParvularia() {
    const [drafts, setDrafts] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        (async () => {
            try {
                const r = await draftsAPI.getAll();
                setDrafts(r.data || []);
            } catch (_) { setDrafts([]); }
            finally { setLoading(false); }
        })();
    }, []);

    const stats = useMemo(() => {
        const porRubrica = {};
        const porModulo = {};
        INDICADORES_PARVULARIA.forEach((r) => {
            const est = computeEstadoRubrica(drafts, r.id);
            porRubrica[r.id] = { ...r, ...est };
            porModulo[r.modulo] = porModulo[r.modulo] || { total: 0, iniciadas: 0, analizadas: 0, listas: 0 };
            porModulo[r.modulo].total += 1;
            if (est.estado !== 'no_iniciada') porModulo[r.modulo].iniciadas += 1;
            if (est.estado === 'analizada' || est.estado === 'lista') porModulo[r.modulo].analizadas += 1;
            if (est.estado === 'lista') porModulo[r.modulo].listas += 1;
        });
        const total = INDICADORES_PARVULARIA.length;
        const iniciadas = Object.values(porRubrica).filter((r) => r.estado !== 'no_iniciada').length;
        const analizadas = Object.values(porRubrica).filter((r) => ['analizada', 'lista'].includes(r.estado)).length;
        const listas = Object.values(porRubrica).filter((r) => r.estado === 'lista').length;
        const avance = total === 0 ? 0 : Math.round((iniciadas / total) * 100);
        return { porRubrica, porModulo, total, iniciadas, analizadas, listas, avance };
    }, [drafts]);

    return (
        <Layout>
            <div className="space-y-6 animate-fade-in" data-testid="progreso-parvularia">
                {/* Hero */}
                <div className="rounded-2xl border border-slate-200 bg-gradient-to-br from-pink-50 via-white to-sky-50 p-6">
                    <div className="flex items-start gap-3">
                        <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-pink-500 text-white">
                            <Baby className="h-6 w-6" />
                        </div>
                        <div>
                            <div className="text-[10px] font-bold uppercase tracking-widest text-pink-700">Progreso 2026</div>
                            <h1 className="mt-1 font-heading text-2xl sm:text-3xl font-bold text-slate-900">
                                Mi progreso · Educación Parvularia
                            </h1>
                            <p className="mt-2 text-sm text-slate-600 max-w-3xl leading-relaxed">
                                Este panel muestra tu avance personal en los 3 módulos oficiales de Parvularia 2026.
                                Cada rúbrica actualiza su estado automáticamente cuando trabajas un borrador y lo analizas con IA.
                            </p>
                        </div>
                    </div>
                </div>

                {/* Avance global + KPIs */}
                <div className="grid gap-3 md:grid-cols-4">
                    <Card>
                        <CardContent className="p-4">
                            <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500">Avance global</div>
                            <div className="mt-1 font-mono text-3xl font-extrabold text-indigo-700" data-testid="avance-global">{stats.avance}%</div>
                            <div className="mt-1.5 h-2 rounded-full bg-slate-100 overflow-hidden">
                                <div className="h-full bg-indigo-500 transition-all" style={{ width: `${stats.avance}%` }} />
                            </div>
                            <div className="mt-1 text-[11px] text-slate-500">{stats.iniciadas} de {stats.total} rúbricas iniciadas</div>
                        </CardContent>
                    </Card>
                    <Card>
                        <CardContent className="p-4">
                            <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500">Analizadas con IA</div>
                            <div className="mt-1 font-mono text-3xl font-extrabold text-slate-900">{stats.analizadas}</div>
                            <div className="text-[11px] text-slate-500">De {stats.total} rúbricas</div>
                        </CardContent>
                    </Card>
                    <Card>
                        <CardContent className="p-4">
                            <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500">Listas (C/D)</div>
                            <div className="mt-1 font-mono text-3xl font-extrabold text-emerald-700">{stats.listas}</div>
                            <div className="text-[11px] text-slate-500">Competente o Destacado</div>
                        </CardContent>
                    </Card>
                    <Card>
                        <CardContent className="p-4">
                            <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500">Total borradores</div>
                            <div className="mt-1 font-mono text-3xl font-extrabold text-slate-900">{drafts.length}</div>
                            <div className="text-[11px] text-slate-500">De Parvularia</div>
                        </CardContent>
                    </Card>
                </div>

                {/* Avance por módulo */}
                <Card>
                    <CardContent className="p-5">
                        <h2 className="font-heading text-lg font-bold text-slate-900 mb-3">Avance por módulo</h2>
                        <div className="grid gap-3 md:grid-cols-3" data-testid="avance-por-modulo">
                            {MODULOS_PARVULARIA.map((m) => {
                                const mstat = stats.porModulo[m.id] || { total: 0, iniciadas: 0, analizadas: 0, listas: 0 };
                                const pct = mstat.total === 0 ? 0 : Math.round((mstat.iniciadas / mstat.total) * 100);
                                const colorCls = m.color === 'indigo' ? 'bg-indigo-500' : m.color === 'emerald' ? 'bg-emerald-500' : 'bg-amber-500';
                                return (
                                    <div key={m.id} className="rounded-xl border border-slate-200 p-4">
                                        <div className="flex items-center justify-between">
                                            <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500">{m.nombre}</div>
                                            <span className="font-mono text-xs font-bold text-slate-900">{pct}%</span>
                                        </div>
                                        <div className="mt-1 text-sm font-bold text-slate-800 leading-snug">{m.titulo}</div>
                                        <div className="mt-2 h-2 rounded-full bg-slate-100 overflow-hidden">
                                            <div className={`h-full ${colorCls} transition-all`} style={{ width: `${pct}%` }} />
                                        </div>
                                        <div className="mt-2 flex justify-between text-[11px] text-slate-500">
                                            <span>{mstat.iniciadas}/{mstat.total} iniciadas</span>
                                            <span>{mstat.listas} listas</span>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </CardContent>
                </Card>

                {/* Estado por rúbrica */}
                <Card>
                    <CardContent className="p-5">
                        <div className="flex items-center justify-between mb-3">
                            <h2 className="font-heading text-lg font-bold text-slate-900">Estado por rúbrica</h2>
                            <Link to="/borradores">
                                <Button size="sm" variant="outline" className="gap-1 text-xs">Ir a borradores <ChevronRight className="h-3 w-3" /></Button>
                            </Link>
                        </div>
                        <div className="space-y-3" data-testid="estado-rubricas">
                            {MODULOS_PARVULARIA.map((m) => {
                                const rubricas = INDICADORES_PARVULARIA.filter((r) => r.modulo === m.id);
                                if (rubricas.length === 0) return null;
                                return (
                                    <div key={m.id}>
                                        <div className="flex items-center gap-2 mb-2">
                                            <Badge className={m.color === 'indigo' ? 'bg-indigo-600 text-white' : m.color === 'emerald' ? 'bg-emerald-600 text-white' : 'bg-amber-600 text-white'}>
                                                {m.nombre}
                                            </Badge>
                                            <div className="text-xs font-medium text-slate-700">{m.titulo}</div>
                                        </div>
                                        <div className="space-y-1">
                                            {rubricas.map((r) => {
                                                const st = stats.porRubrica[r.id];
                                                const style = ESTADO_STYLE[st.estado];
                                                const Icon = style.icon;
                                                const rStyle = NIVEL_RIESGO_STYLE[r.riesgo] || NIVEL_RIESGO_STYLE['Medio'];
                                                return (
                                                    <div key={r.id} className="flex items-center gap-3 rounded-lg border border-slate-200 bg-white p-3 hover:bg-slate-50 transition" data-testid={`rubrica-row-${r.id}`}>
                                                        <Icon className={`h-4 w-4 shrink-0 ${style.color === 'emerald' ? 'text-emerald-600' : style.color === 'indigo' ? 'text-indigo-600' : style.color === 'amber' ? 'text-amber-600' : 'text-slate-400'}`} />
                                                        <div className="flex-1 min-w-0">
                                                            <div className="flex items-center gap-2 flex-wrap">
                                                                <span className="font-mono text-[10px] font-bold text-slate-500">{r.codigo}</span>
                                                                <span className={`text-[10px] font-bold uppercase tracking-wider ${style.color === 'emerald' ? 'text-emerald-700' : style.color === 'indigo' ? 'text-indigo-700' : style.color === 'amber' ? 'text-amber-700' : 'text-slate-500'}`}>
                                                                    {style.label}
                                                                </span>
                                                                {st.nivel && <Badge className="bg-emerald-600 text-white text-[9px]">{st.nivel}</Badge>}
                                                                <Badge className={`${RIESGO_BADGE[rStyle.color]} border text-[9px]`}>{rStyle.emoji} {rStyle.label}</Badge>
                                                            </div>
                                                            <div className="text-xs font-medium text-slate-900 leading-tight">{r.nombre}</div>
                                                            <div className="text-[10px] text-slate-500">
                                                                Nacional 2025: {r.logrado_2025}% Logrado · {st.count} borrador{st.count === 1 ? '' : 'es'}
                                                            </div>
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </CardContent>
                </Card>

                {/* Nota separación */}
                <div className="rounded-xl border border-slate-200 bg-slate-50 p-3 text-[11px] text-slate-500" data-testid="separacion-nota">
                    <div className="flex items-start gap-2">
                        <AlertTriangle className="h-3 w-3 mt-0.5 shrink-0" />
                        <div>
                            <strong>Mi progreso</strong> refleja únicamente tu trabajo dentro de la plataforma.
                            <strong className="ml-1">Resultado nacional 2025</strong> muestra el porcentaje de educadoras que
                            lograron ese indicador a nivel país — es contexto, no una calificación tuya.
                        </div>
                    </div>
                </div>
            </div>
        </Layout>
    );
}
